import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { assets } from "../../data/sep26Data";
import { EASE_OUT } from "./hooks";

const LINKS = [
  { label: "Historia", href: "#historia" },
  { label: "Competición", href: "#competicion" },
  { label: "Match Day", href: "#partido" },
  { label: "Estadísticas", href: "#estadisticas" },
  { label: "Plantilla", href: "#plantilla" },
  { label: "Traje", href: "#traje" },
  { label: "Galería", href: "#galeria" },
  { label: "Multimedia", href: "#multimedia" },
];

export const Nav26 = () => {
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setSolid(y > 40);
    setHidden(y > 240 && y > prev && !open);
  });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="s26-nav"
        className="fixed inset-x-0 top-0 z-50"
        style={{
          transform: hidden ? "translateY(-100%)" : "translateY(0)",
          transition: "transform 320ms var(--ease-out), background-color 400ms ease, border-color 400ms ease",
          backgroundColor: solid ? "rgba(7,10,28,0.82)" : "transparent",
          backdropFilter: solid ? "blur(14px)" : "none",
          borderBottom: `1px solid ${solid ? "var(--line)" : "transparent"}`,
        }}
      >
        <div className="wrap flex items-center justify-between py-3">
          <a href="#inicio" data-testid="s26-logo" className="flex items-center gap-3" aria-label="Arsenal de Allende, inicio">
            <img src={assets.crest} alt="" width="40" height="47" className="h-11 w-auto" />
            <span className="display text-[1.35rem] leading-none hidden sm:block">
              Arsenal<br />de Allende
            </span>
          </a>

          <nav className="hidden xl:flex items-center" aria-label="Principal">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} className="nav-link" data-testid={`s26-nav-${l.href.slice(1)}`}>
                {l.label}
              </a>
            ))}
            <a href="/temporadaAgosto26" className="nav-link" data-testid="s26-nav-anterior">
              Anterior
            </a>
          </nav>

          <button
            type="button"
            className="xl:hidden grid h-11 w-11 place-items-center border"
            style={{ borderColor: "var(--line)" }}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            data-testid="s26-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="s26-menu"
            className="fixed inset-0 z-40 flex flex-col justify-center gap-2 px-8"
            style={{ background: "var(--navy-deep)" }}
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.45, ease: [0.77, 0, 0.175, 1] }}
          >
            {[...LINKS, { label: "Temporada anterior", href: "/temporadaAgosto26" }].map((l, i) => (
              <motion.a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="display text-[clamp(44px,13vw,84px)]"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.12 + i * 0.05 }}
              >
                {l.label}
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
